import { useMemo, useState } from "react"
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { EmptyContent, EmptyContentVariant } from "../../../../../../../shared/components/skeleton/EmptyContent"
import { DatePeriod } from "../../../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { ReadonlyOrder } from "../../../weekMenuTypes"
import { UserMeetingService } from "../../../../upcomingOrders/meetingService/page/MeetingService"

import { useListPagination } from "../../../../../../../shared/hooks/useListPagination"
import { MeetingListPagination } from "../../../../../../serviceCenter/report/home/components/meetigs/MeetingListPagination"
import { UpcomingOrdersPanel } from "../UpcomingOrdersPanel"
import { UpcomingOrdersModal } from "../UpcomingOrdersModal"
import { groupMeetingsByDay } from "./meetingDayGroups"
import { MeetingsDayList } from "./MeetingsDayList"

type MeetingsPanelProps = Readonly<{
    meetings: ReadonlyOrder[]
    period: DatePeriod
}>

export function MeetingsPanel({ meetings, period }: MeetingsPanelProps) {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const { pagedItems, pagination } = useListPagination(meetings)

    const groups = useMemo(
        () => groupMeetingsByDay(pagedItems, dateTimeFactory, l10n),
        [pagedItems, dateTimeFactory, l10n],
    )

    return (
        <>
            <LocalizedStrict
                id="user-home-meetings-panel-actions"
                attrs={{ collapseAriaLabel: true, expandAriaLabel: true }}
            >
                <UpcomingOrdersPanel
                    icon={faCalendarDays}
                    iconBackgroundColor={tailwindPalette.blue[100]}
                    iconColor={tailwindPalette.blue[400]}
                    titleId="user-home-upcoming-meetings"
                    titleFallback="Meetings"
                    counter={pagination.totalItems}
                    onShowAll={() => setIsModalOpen(true)}
                    collapseAriaLabel=""
                    expandAriaLabel=""
                    footer={pagination.totalItems > 0 ? <MeetingListPagination {...pagination} /> : undefined}
                >
                    {!groups.length ? (
                        <LocalizedStrict id="user-home-upcoming-meetings-no-meetings" attrs={{ title: true }}>
                            <EmptyContent variant={EmptyContentVariant.SINGLE_LEFT} title="No meetings in period" />
                        </LocalizedStrict>
                    ) : (
                        <MeetingsDayList groups={groups} meetings={meetings} />
                    )}
                </UpcomingOrdersPanel>
            </LocalizedStrict>
            {isModalOpen && (
                <LocalizedStrict id="user-home-upcoming-meetings-modal-title" attrs={{ title: true }}>
                    <UpcomingOrdersModal open onClose={() => setIsModalOpen(false)} title="Meetings">
                        <UserMeetingService initialPeriod={period} />
                    </UpcomingOrdersModal>
                </LocalizedStrict>
            )}
        </>
    )
}
